/**
 * MigrationResourcesSidebar
 *
 * Right-side "Quick Guides" sidebar shown ONLY in the migration
 * assistant's layout. Ported from the legacy ``csg-innv-agent-ui-cf``
 * ``ResourcesSidebar``, adapted to Chakra v1.8.9 (no ``colorPalette``,
 * no ``textStyle`` — replaced with v1 equivalents).
 *
 * Layout:
 *   - Top: a green "Migration overview" button that navigates to
 *     ``paths.MigrationOverview``.
 *   - Below: a card with 4 grouped sections of external doc links
 *     (Getting Started / Teams & Users / Schedules & Escalations /
 *     Services & Events) — opens in a new tab.
 *
 * Behavior:
 *   - Collapsible via the standard ``isCollapsed`` / ``onToggle`` props
 *     that all sidebars in this app accept (see ``SidebarProps`` in
 *     layout-config.tsx). The vertical toggle bar matches the other
 *     sidebars' look so the UI is consistent.
 *   - On mobile (``base`` breakpoint) the sidebar is hidden entirely.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Flex, Heading, Link, Text, VStack } from '@chakra-ui/react';
import { Info, PanelLeftOpen } from 'lucide-react';

import { paths } from '@/config/paths';

interface ResourceItem {
  title: string;
  link: string;
}

interface ResourceSection {
  title: string;
  items: ResourceItem[];
}

/**
 * Doc links lifted verbatim from the legacy project so the user gets
 * the same set they had before. URLs are public PD docs / community.
 */
const SECTIONS: ResourceSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        title: 'OpsGenie Migration Best Practices',
        link: 'https://community.pagerduty.com/announcements-6/welcome-to-your-opsgenie-to-pagerduty-migration-journey-622',
      },
      {
        title: 'Platform Comparison Guide',
        link: 'https://www.pagerduty.com/resources/aiops/ebook/the-complete-post-opsgenie-survival-handbook/',
      },
      {
        title: 'Quick Start Checklist',
        link: 'https://www.pagerduty.com/resources/aiops/checklist/opsgenie-migration-checklist/',
      },
    ],
  },
  {
    title: 'Teams & Users',
    items: [
      {
        title: 'User Management Guide',
        link: 'https://support.pagerduty.com/main/docs/manage-users',
      },
      {
        title: 'Team Configuration',
        link: 'https://support.pagerduty.com/main/docs/teams',
      },
      {
        title: 'Permissions & Roles',
        link: 'https://support.pagerduty.com/main/docs/user-roles',
      },
    ],
  },
  {
    title: 'Schedules & Escalations',
    items: [
      {
        title: 'On-Call Schedules Guide',
        link: 'https://support.pagerduty.com/main/docs/schedule-basics',
      },
      {
        title: 'Schedule Examples',
        link: 'https://support.pagerduty.com/main/docs/schedule-examples',
      },
      {
        title: 'Escalation Policies',
        link: 'https://support.pagerduty.com/main/docs/escalation-policies',
      },
      {
        title: 'Escalation Policies and Schedules',
        link: 'https://support.pagerduty.com/main/docs/escalation-policies-and-schedules',
      },
      {
        title: 'Handoff Best Practices',
        link: 'https://support.pagerduty.com/main/docs/user-profile#on-call-handoff-notifications',
      },
    ],
  },
  {
    title: 'Services & Events',
    items: [
      {
        title: 'Service Configuration',
        link: 'https://support.pagerduty.com/main/docs/services-and-integrations',
      },
      {
        title: 'Event Orchestration',
        link: 'https://support.pagerduty.com/main/docs/event-orchestration',
      },
      {
        title: 'Monitoring Integrations',
        link: 'https://www.pagerduty.com/integrations/',
      },
    ],
  },
];

const PAGERDUTY_GREEN = '#048a24';

interface MigrationResourcesSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const MigrationResourcesSidebar: React.FC<MigrationResourcesSidebarProps> = ({
  isCollapsed,
  onToggle,
}) => {
  const navigate = useNavigate();

  const goToOverview = () => navigate(paths.MigrationOverview);

  return (
    <Box
      w={isCollapsed ? '50px' : '320px'}
      minW="50px"
      h="100%"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      p={isCollapsed ? 2 : 6}
      overflowY="auto"
      overflowX="hidden"
      position="relative"
      transition="all 0.3s ease-in-out"
      display={{ base: 'none', md: 'block' }}
    >
      {/* Top "Migration overview" CTA — present in both states; collapsed
          mode shows just the icon. */}
      {isCollapsed ? (
        <Flex direction="column" align="center" mt={2} mb={4}>
          <Button
            aria-label="Migration overview"
            onClick={goToOverview}
            size="sm"
            bg={PAGERDUTY_GREEN}
            color="white"
            _hover={{ bg: 'green.700' }}
            p={0}
            minW="36px"
            h="36px"
          >
            <Info size={16} />
          </Button>
        </Flex>
      ) : (
        <Button
          w="full"
          onClick={goToOverview}
          bg={PAGERDUTY_GREEN}
          color="white"
          _hover={{ bg: 'green.700' }}
          mb={5}
          leftIcon={<Info size={16} />}
        >
          Migration overview
        </Button>
      )}

      {/* Quick Guides card — hidden when collapsed to match the legacy
          UX (the legacy sidebar uses the same collapse-then-icon-only
          treatment). */}
      {!isCollapsed && (
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
        >
          <Flex align="center" mb={3} color="gray.700">
            <PanelLeftOpen size={16} />
            <Heading as="h3" size="sm" ml={2} fontWeight="semibold">
              Quick Guides
            </Heading>
          </Flex>

          <VStack align="stretch" spacing={5}>
            {SECTIONS.map((section) => (
              <Box key={section.title}>
                <Text
                  color={PAGERDUTY_GREEN}
                  fontWeight="semibold"
                  fontSize="sm"
                  mb={2}
                >
                  {section.title}
                </Text>
                <VStack align="stretch" spacing={1}>
                  {section.items.map((item) => (
                    <Link
                      key={item.title}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      fontSize="sm"
                      color="gray.600"
                      _hover={{ textDecoration: 'underline', color: 'gray.800' }}
                    >
                      {item.title}
                    </Link>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* Toggle bar — same look as the other sidebars in the app
          (left edge, vertical line). Hidden on mobile because the
          sidebar itself is hidden there. */}
      <Box
        position="absolute"
        top="50%"
        left={0}
        transform="translateY(-50%)"
        zIndex={10}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="0 8px 8px 0"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>
    </Box>
  );
};

export default MigrationResourcesSidebar;
